import type { RouteObject } from 'react-router-dom';
import { PermissionRoute } from '@/features/auth';
import { FILL_HEIGHT_PAGE, FULL_WIDTH_PAGE } from '@/shared/layouts/pageLayout';
import { DashboardSearch } from '@/features/dashboard';
import { AgreementDetail } from '../pages/AgreementDetail';
import { AgreementByQuote } from '../pages/AgreementByQuote';
import { AgreementCustomerEdit } from '../pages/AgreementCustomerEdit';
import { AgreementCustomerNameEdit } from '../pages/AgreementCustomerNameEdit';
import { ThirdPartyAgreementDetail } from '../pages/ThirdPartyAgreementDetail';
import { AgreementAccessGate } from '../components/access/AgreementAccessGate';

// No route-matching ambiguity: `agreements/quote/:quoteId` is two segments and
// `agreements/:id` is one, so they cannot collide. (Even if they could, React
// Router v6 ranks the static `quote` segment higher.)
//
// UI gating is convenience only (CLAUDE.md Power of Ten rule 9). The backend
// `permission:agreement.read` / `agreement.browse` middleware is the security
// boundary; PermissionRoute only avoids showing a page that would 403.
//
// AgreementAccessGate is the same kind of convenience for the access justification:
// every page under `:id` reads a route the agreements-service gate can refuse.
export const agreementsRoutes: RouteObject[] = [
    {
        path: 'agreements',
        children: [
            {
                element: (
                    <PermissionRoute
                        requiredPermissions={[
                            { resource: 'quote', action: 'browse' },
                            { resource: 'plan', action: 'browse' },
                        ]}
                    />
                ),
                children: [
                    {
                        index: true,
                        handle: FILL_HEIGHT_PAGE,
                        element: (
                            <DashboardSearch
                                title="Agreements"
                                subtitle="Search and manage available quotes and agreements across all of your available dealerships."
                            />
                        ),
                    },
                ],
            },
            {
                element: <PermissionRoute requiredPermissions={[{ resource: 'agreement', action: 'browse' }]} />,
                children: [{ path: 'quote/:quoteId', element: <AgreementByQuote /> }],
            },
            {
                element: <PermissionRoute requiredPermissions={[{ resource: 'third_party_agreement', action: 'browse' }]} />,
                children: [{ path: 'third-party', element: <ThirdPartyAgreementDetail />, handle: FULL_WIDTH_PAGE }],
            },
            {
                element: <PermissionRoute requiredPermissions={[{ resource: 'agreement', action: 'read' }]} />,
                children: [
                    {
                        path: ':id',
                        handle: FULL_WIDTH_PAGE,
                        element: (
                            <AgreementAccessGate fullWidthPage>
                                <AgreementDetail />
                            </AgreementAccessGate>
                        ),
                    },
                    // customer.edit is enforced by the page itself and by the
                    // backend; agreement.read is what reaches the plan at all.
                    { path: ':id/customer/edit', element: <AgreementAccessGate><AgreementCustomerEdit /></AgreementAccessGate> },
                    // customer.edit plus customer.change_name, enforced the same way; no dealer role holds the latter.
                    { path: ':id/customer/name/edit', element: <AgreementAccessGate><AgreementCustomerNameEdit /></AgreementAccessGate> },
                ],
            },
        ],
    },
];
